export const TOTAL_LEVELS = 30;

/** Level 1 is already tight. Level 30 is a thin, fast wedge. */
export const SECTOR_START_DEG = 56;
export const SECTOR_END_DEG = 18;
/** Degrees per second. Early levels start fast so they are not free. */
export const SPEED_START_DPS = 190;
export const SPEED_END_DPS = 340;

export const clampLevel = (level) => {
  const n = Number(level);
  if (!Number.isFinite(n)) return 1;
  return Math.min(TOTAL_LEVELS, Math.max(1, Math.round(n)));
};

export const getLevelConfig = (level) => {
  const lv = clampLevel(level);
  const t = (lv - 1) / (TOTAL_LEVELS - 1);
  const eased = t ** 0.72;
  const sectorDeg =
    Math.round((SECTOR_START_DEG + (SECTOR_END_DEG - SECTOR_START_DEG) * eased) * 10) / 10;
  const speedDps = Math.round(SPEED_START_DPS + (SPEED_END_DPS - SPEED_START_DPS) * eased);
  return { level: lv, sectorDeg, speedDps };
};

export const normalizeAngle = (degrees) => {
  let angle = degrees % 360;
  if (angle < 0) angle += 360;
  return angle;
};

export const angularError = (rotationDeg) => {
  let err = normalizeAngle(rotationDeg);
  if (err > 180) err = 360 - err;
  return err;
};

export const isTargetHit = (errorDeg, sectorDeg) => errorDeg <= sectorDeg / 2;

export const scoreFromAngleError = (errorDeg, sectorDeg) => {
  const halfZone = sectorDeg / 2;
  if (errorDeg <= halfZone) {
    return Math.round(100 - (errorDeg / halfZone) * 8);
  }
  const beyond = errorDeg - halfZone;
  const falloffRange = Math.max(1, 90 - halfZone);
  return Math.max(0, Math.round(92 - (beyond / falloffRange) * 92));
};

const pick = (lines) => lines[Math.floor(Math.random() * lines.length)];

const MISS_RANKS = [
  {
    max: 29,
    animal: 'Snail',
    messages: [
      "Snail rank! 🐌 Did you take a nap while it spun? That stop was a different time zone from the target.",
      "Snail rank! 🐌 You clicked like the wheel owed you rent. The purple zone filed a restraining order.",
      "Snail rank! 🐌 Reflexes of a screensaver. Even your grandma’s timing would land closer.",
    ],
  },
  {
    max: 49,
    animal: 'Turtle',
    messages: [
      "Turtle mode! 🐢 Slow, muddy, and proud of it. You remembered there was a target. That’s the highlight.",
      "Turtle mode! 🐢 You stopped the wheel in a zip code the target doesn’t even visit.",
      "Turtle mode! 🐢 That wasn’t a stop. That was a polite wave at the sector as it went by.",
    ],
  },
  {
    max: 69,
    animal: 'Rabbit',
    messages: [
      "Rabbit rank! 🐇 Mid at best. Don’t get hoppy — “near the vibe of purple” isn’t hitting purple.",
      "Rabbit rank! 🐇 Average timing with extra confidence. The pointer watched you miss and took notes.",
      "Rabbit rank! 🐇 You high-fived the air and called it accuracy. It wasn’t.",
    ],
  },
  {
    max: 84,
    animal: 'Wolf',
    messages: [
      "Wolf rank? 🐺 Barely. A little above average isn’t a personality. The pack would scroll past this miss and not look back.",
      "Wolf rank? 🐺 Close enough to embarrass yourself. You almost hit it, which is a fancy way of saying you didn’t.",
      "Wolf rank? 🐺 The sector felt that breeze. Almost doesn’t count, and neither does this.",
    ],
  },
  {
    max: Infinity,
    animal: 'Cheetah',
    messages: [
      "Cheetah? 🐆 Don’t. Close-ish isn’t a high score. You almost had it, which is a fancy way of saying you missed. Come back at 95 — inside the zone.",
      "Cheetah? 🐆 You grazed the idea of the target. Grazing doesn’t clear the level. Come back when you actually stop on it.",
    ],
  },
];

const HIT_RANKS = [
  {
    max: 94,
    animal: 'Cheetah',
    messages: [
      "Cheetah? 🐆 Don’t. You clipped the zone like a tourist clipping a curb. Clearing the level isn’t a personality. Come back at 95.",
      "Cheetah? 🐆 Inside the sector, sure. Close-ish isn’t a high score. You almost had a real stop, which is a fancy way of saying you didn’t.",
      "Cheetah? 🐆 The wheel let you live. Don’t frame a 90-something. Come back at 95.",
    ],
  },
  {
    max: 99,
    animal: 'Falcon',
    messages: [
      "Falcon-level timing! 🦅 That’s actually impressive. You saw the sector and proved it. One more nudge for a perfect 100?",
      "Falcon-level stop! 🦅 Rare praise: you earned it. The pointer actually recognized you.",
    ],
  },
  {
    max: Infinity,
    animal: 'Peregrine Falcon',
    messages: [
      "Peregrine Falcon! 🦅 Perfect stop. Are you even human? That wheel didn’t stand a chance.",
      "Peregrine Falcon! 🦅 Dead center energy. The sector didn’t stand a chance.",
    ],
  },
];

const CLEAR_ALL_PRAISE = [
  " All 30 levels, too. That’s actually scary.",
  " And you cleared the whole 30-level gauntlet. Are you even human?",
];

const CLEAR_ALL_ROAST = [
  " You beat every level and still couldn’t land a 95. Dedication and disappointment, holding hands.",
  " Thirty levels cleared and the last stop still isn’t praise-worthy. Persistence isn’t precision.",
];

const pickRank = (bands, score) => {
  const n = Number(score);
  const value = Number.isFinite(n) ? n : 0;
  return bands.find((entry) => value <= entry.max) || bands[bands.length - 1];
};

export const getWheelRank = ({ score, hit, clearedAll } = {}) => {
  const bands = hit ? HIT_RANKS : MISS_RANKS;
  const rank = pickRank(bands, score);
  let message = pick(rank.messages);

  if (clearedAll) {
    message += pick(hit && score >= 95 ? CLEAR_ALL_PRAISE : CLEAR_ALL_ROAST);
  }

  return {
    animal: rank.animal,
    message,
  };
};

/** Result copy. Pass `{ hit }` so a 92–94 clear still gets roasted. */
export const scoreLabel = (score, extras = {}) =>
  getWheelRank({ score, hit: extras.hit, clearedAll: extras.clearedAll }).message;
